import React from 'react';
import { Shield, Wind, Activity, Layers, Info } from 'lucide-react';

const HELMET_TYPES = [
  {
    name: 'JET / Abierto',
    description: 'Libertad y frescura para la ciudad',
    primaryUse: 'Ciudad',
    stats: {
      protection: 60,
      comfort: 95,
      ventilation: 100,
      versatility: 70
    },
    color: '#FF7F32'
  },
  {
    name: 'Integral',
    description: 'Máxima seguridad para carretera',
    primaryUse: 'Carretera / Racing',
    stats: {
      protection: 100,
      comfort: 80,
      ventilation: 75,
      versatility: 85
    },
    color: '#061970'
  },
  {
    name: 'Modular',
    description: 'Lo mejor de dos mundos',
    primaryUse: 'Turismo',
    stats: {
      protection: 90,
      comfort: 90,
      ventilation: 85,
      versatility: 100
    },
    color: '#459EDF'
  },
  {
    name: 'Cross / Off-road',
    description: 'Para terrenos difíciles',
    primaryUse: 'Off-road',
    stats: {
      protection: 95,
      comfort: 70,
      ventilation: 95,
      versatility: 60
    },
    color: '#10B981'
  }
];

interface ComparisonSectionProps {
    onCategorySelect: (category: string) => void;
}

export const ComparisonSection: React.FC<ComparisonSectionProps> = ({ onCategorySelect }) => {
  return (
    <div className="bg-[#F9FAFB] py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-[#FF7F32] font-bold uppercase tracking-widest text-sm mb-2 block">Guía de Selección</span>
          <h2 className="text-4xl font-black italic text-[#061970] uppercase tracking-tighter mb-4">
            Comparativa de <span className="text-[#459EDF]">Rendimiento</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Analiza qué tipo de casco se adapta mejor a tu estilo de conducción basándote en los 5 pilares fundamentales.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {HELMET_TYPES.map((type, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 shadow-xl border-t-4 hover:-translate-y-2 transition-transform duration-300" style={{ borderColor: type.color }}>
              <div className="mb-6">
                <h3 className="text-2xl font-black italic uppercase text-gray-800 mb-2">{type.name}</h3>
                <p className="text-sm text-gray-500 font-medium mb-4">{type.description}</p>
                <div className="inline-block bg-gray-100 px-3 py-1 rounded-full">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-600">
                    Uso: <span style={{ color: type.color }}>{type.primaryUse}</span>
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                {/* Protection */}
                <div>
                  <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-1">
                    <span className="flex items-center gap-1 text-gray-500"><Shield className="w-3 h-3" /> Protección</span>
                    <span className="text-gray-800">{type.stats.protection}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${type.stats.protection}%`, backgroundColor: type.color }}
                    ></div>
                  </div>
                </div>

                {/* Comfort */}
                <div>
                  <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-1">
                    <span className="flex items-center gap-1 text-gray-500"><Layers className="w-3 h-3" /> Confort</span>
                    <span className="text-gray-800">{type.stats.comfort}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${type.stats.comfort}%`, backgroundColor: type.color }}
                    ></div>
                  </div>
                </div>

                {/* Ventilation */}
                <div>
                  <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-1">
                    <span className="flex items-center gap-1 text-gray-500"><Wind className="w-3 h-3" /> Ventilación</span>
                    <span className="text-gray-800">{type.stats.ventilation}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${type.stats.ventilation}%`, backgroundColor: type.color }}
                    ></div>
                  </div>
                </div>

                {/* Versatility */}
                <div>
                  <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-1">
                    <span className="flex items-center gap-1 text-gray-500"><Activity className="w-3 h-3" /> Versatilidad</span>
                    <span className="text-gray-800">{type.stats.versatility}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${type.stats.versatility}%`, backgroundColor: type.color }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-100">
                <button 
                  onClick={() => onCategorySelect(type.name)}
                  className="w-full py-3 rounded-lg font-black uppercase text-sm tracking-widest transition-all duration-300 border-2 hover:shadow-lg active:scale-95"
                  style={{ borderColor: type.color, color: type.color }}
                  onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = type.color;
                      e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = type.color;
                  }}
                >
                  Ver Catálogo
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
